// src/components/DeviceLocations/DeviceLocations.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const DeviceLocations = () => {
  const [deviceIds, setDeviceIds] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState('');
  const [details, setDetails] = useState(null);

  const fetchDeviceLocations = async () => {
    const newRequestId = uuidv4();
    setRequestId(newRequestId);
    setLoading(true);
    setError(null);
    setLocations([]);
    setDetails(null);

    try {
      // Validate input
      if (!deviceIds.trim()) {
        throw {
          custom: true,
          message: "Device IDs are required",
          details: { 
            required_fields: ["deviceIds"]
          }
        };
      }

      const deviceIdsArray = deviceIds.split(',').map(id => id.trim());
      console.log('Request details:', {
        deviceCount: deviceIdsArray.length,
        sampleDeviceId: deviceIdsArray[0]
      });

      // Fetch token from environment variable
      const token = "3585535a-137c-499e-82ad-af9feff7d8e7";

      if (!token) {
        throw {
          custom: true,
          message: "Token is missing from environment variables",
          details: {
            required_fields: ["REACT_APP_TOKEN_ID"]
          }
        };
      }

      const endpoints = [
        process.env.REACT_APP_LBS_ENDPOINT_1 || "http://icloud.assetscontrols.com:8092/OpenApi/LBS",
        process.env.REACT_APP_LBS_ENDPOINT_2 || "https://icloud.assetscontrols.com:3443/OpenApi/LBS"
      ];

      // Try both FType values (1 for assets, 2 for devices)
      const tryFTypes = [1, 2];
      let lastResponse = null;
      let successfulData = null;

      for (const endpoint of endpoints) {
        for (const fType of tryFTypes) {
          try {
            const payload = {
              FAction: "QueryLBSMonitorListByFGUIDs",
              FTokenID: token,
              FGUIDs: deviceIdsArray.join(','),
              FType: fType
            };

            console.log(`Trying endpoint: ${endpoint} with FType=${fType}`);
            const response = await axios.post(endpoint, payload, {
              headers: {
                "Content-Type": "application/json",
                "X-Request-ID": newRequestId
              },
              timeout: 5000
            });

            lastResponse = response.data;
            console.log('API Response:', JSON.stringify(response.data, null, 2));

            if (response.data?.Result === 200) {
              if (response.data.FObject && response.data.FObject.length > 0) {
                // Check if data is recent
                const locationsData = response.data.FObject.map(device => {
                  const gpsTime = device.FGPSTime ? new Date(device.FGPSTime) : null;
                  const isRecent = gpsTime ? (new Date() - gpsTime) < 24 * 60 * 60 * 1000 : false;
                  
                  return {
                    assetId: device.FAssetID,
                    coordinates: {
                      longitude: device.FLongitude,
                      latitude: device.FLatitude
                    },
                    timestamps: {
                      gpsTime: device.FGPSTime,
                      receivedTime: device.FRecvTime,
                      isRecent: isRecent
                    },
                    speed: device.FSpeed,
                    direction: device.FDir,
                    status: device.FStatus
                  };
                });

                successfulData = {
                  data: locationsData,
                  details: {
                    fTypeUsed: fType,
                    endpointUsed: endpoint
                  }
                };

                // Check device status if we have empty but valid response
                if (locationsData.length < deviceIdsArray.length) {
                  try {
                    const statusPayload = {
                      FAction: "QueryDeviceStatus",
                      FTokenID: token,
                      FAssetID: deviceIdsArray[0]
                    };
                    
                    const statusResponse = await axios.post(endpoint, statusPayload, {
                      headers: {
                        "Content-Type": "application/json"
                      },
                      timeout: 3000
                    });

                    successfulData.details.deviceStatus = statusResponse.data;
                    console.log('Device status check:', statusResponse.data);
                  } catch (statusError) {
                    console.log('Device status check failed:', statusError.message);
                  }
                }

                setLocations(locationsData);
                setDetails(successfulData.details);
                return;
              }
            }
          } catch (error) {
            console.error(`Attempt failed (FType=${fType}):`, error.message);
            if (error.response) {
              console.error('Error response data:', error.response.data);
            }
            continue; // Try next FType
          }
        }
      }

      // If we get here, all attempts failed
      if (lastResponse?.Result === 200 && (!lastResponse.FObject || lastResponse.FObject.length === 0)) {
        throw {
          custom: true,
          message: "Devices found but no location data available",
          details: {
            troubleshootingTips: [
              "Devices may be offline or not reporting",
              "Check if devices have GPS signal",
              "Verify devices are powered on",
              "Some devices only report when movement is detected"
            ],
            lastResponse: lastResponse
          }
        };
      }

      throw {
        custom: true,
        message: "Could not retrieve device location",
        details: {
          lastResponse: lastResponse,
          attemptedFTypes: tryFTypes,
          attemptedEndpoints: endpoints,
          troubleshootingTips: [
            "Verify device IDs exist in the system",
            "Check API token permissions",
            "Confirm devices are properly activated",
            "Contact support if the problem persists"
          ]
        }
      };

    } catch (error) {
      console.error("Error fetching device locations:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="device-locations-container p-4 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Device Locations</h2>
      
      <div className="search-section mb-6 bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Device IDs (comma separated)
            </label>
            <input
              type="text"
              value={deviceIds}
              onChange={(e) => setDeviceIds(e.target.value)}
              placeholder="Enter device IDs separated by commas"
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
        </div>
        
        <button
          onClick={fetchDeviceLocations}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
        >
          {loading ? 'Fetching...' : 'Get Locations'}
        </button>
      </div>

      {loading && (
        <div className="loading-indicator p-4 bg-blue-50 rounded mb-4">
          <p>Fetching device locations...</p>
        </div>
      )}

      {error && (
        <div className="error-message p-4 bg-red-50 border-l-4 border-red-500 rounded mb-4">
          <h3 className="font-bold text-red-700">{error.message}</h3>
          
          {error.details?.troubleshootingTips && (
            <div className="mt-3">
              <h4 className="font-semibold text-sm">Troubleshooting Tips:</h4>
              <ul className="list-disc list-inside ml-4 text-sm">
                {error.details.troubleshootingTips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
          
          {error.details?.lastResponse && (
            <div className="mt-3 p-2 bg-gray-100 rounded text-xs">
              <pre>{JSON.stringify(error.details.lastResponse, null, 2)}</pre>
            </div>
          )}
          
          <div className="mt-3 text-xs text-gray-500">
            Request ID: {requestId}
          </div>
        </div>
      )}

      {locations.length > 0 && (
        <div className="results-container bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-bold">
              Found {locations.length} device locations
              {details && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (FType: {details.fTypeUsed}, Endpoint: {details.endpointUsed})
                </span>
              )}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coordinates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Update
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {locations.map((location, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {location.assetId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {location.coordinates.latitude}, {location.coordinates.longitude}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(location.timestamps.gpsTime).toLocaleString()}
                      </div>
                      <div className={`text-xs ${location.timestamps.isRecent ? 'text-green-600' : 'text-red-600'}`}>
                        {location.timestamps.isRecent ? 'Recent' : 'Stale'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        Speed: {location.speed || 0} km/h
                      </div>
                      <div className="text-xs text-gray-500">
                        Direction: {location.direction || 0}°
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-blue-500 hover:text-blue-700 text-sm">
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {details?.deviceStatus && (
            <div className="p-4 border-t bg-gray-50">
              <h4 className="font-semibold mb-2">Device Status Check:</h4>
              <div className="text-xs bg-gray-100 p-2 rounded">
                <pre>{JSON.stringify(details.deviceStatus, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceLocations;